/**
 * The notes behind an AI report and their photos, shared by `MemoReportForm` and the desktop
 * project record's saved report view: which notes the report covers (its own list, or the filters
 * when generating), the notes with their files, the photos and PDFs among them, the all-photos
 * gallery's state, and downloading the photos as a zip.
 */
import { type AiResponse, type File, FileIdList, Memo } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useMemo, useState } from 'react';

import { useBatchGetAiResponsesSystemQuery } from '../../../../hooks/react-query/useAiResponseReportQuery';
import {
  useMemoClassificationQuery,
  useMemoQuery,
} from '../../../../hooks/react-query/useMemoQuery';
import { useDownloadZipMutation } from '../../../../hooks/react-query/useS3Query';

/** Saves the report's text as a .txt file. */
export const downloadReportText = (reportText: string, aiResponseId?: number) => {
  const blob = new Blob([reportText], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `report_${aiResponseId || Date.now()}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const useReportPhotos = ({
  memoRequest,
  aiResponse,
  enabled = true,
}: {
  memoRequest: Memo;
  aiResponse?: AiResponse;
  enabled?: boolean;
}) => {
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [fileToMemoMap, setFileToMemoMap] = useState<Map<number, Memo>>(new Map());

  // Check if a filter is applied (date range, job/eventId, project scope, department, or idList)
  const hasFilterApplied = useMemo(() => {
    const hasIdList = !!(memoRequest.idList && memoRequest.idList.trim());
    const hasDateRange = !!(
      memoRequest.dateRange &&
      Array.isArray(memoRequest.dateRange) &&
      memoRequest.dateRange.length > 0
    );
    const hasJob = !!(memoRequest.eventId && memoRequest.eventId > 0);
    const hasProjectScope = !!(
      memoRequest.projectPrimaryEventId && memoRequest.projectPrimaryEventId > 0
    );
    const hasDepartment = !!(memoRequest.userDepartmentId && memoRequest.userDepartmentId > 0);
    return hasIdList || hasDateRange || hasJob || hasProjectScope || hasDepartment;
  }, [memoRequest]);

  // Check if we have memoIds to fetch memos
  // A saved report lists its notes in ai_responses_system (memoIds on the response is deprecated),
  // as Build PDF reads them, so viewing a report can show its photos.
  const aiResponsesSystemQuery = useBatchGetAiResponsesSystemQuery({
    aiResponsesId: aiResponse?.id ?? 0,
    enabled: enabled && (aiResponse?.id ?? 0) > 0 && !aiResponse?.memoIds?.trim(),
  });
  const savedReportMemoIds = useMemo(
    () =>
      (aiResponsesSystemQuery.data?.results ?? [])
        .map((row) => row.memoId)
        .filter((id) => id > 0)
        .join(','),
    [aiResponsesSystemQuery.data?.results],
  );

  const hasMemoIds = useMemo(() => {
    if (memoRequest.idList && memoRequest.idList.trim()) return true;
    if (aiResponse?.memoIds && aiResponse.memoIds.trim()) return true;
    return !!savedReportMemoIds;
  }, [memoRequest.idList, aiResponse?.memoIds, savedReportMemoIds]);

  // Create memo request for fetching all memos with files
  // If idList is provided, use that. Otherwise, if aiResponse has memoIds, use that.
  // Otherwise, use the regular filters
  const reportMemoRequest = useMemo(() => {
    const req = Memo.clone(memoRequest);
    req.withoutLimit = true;
    req.includeVideoFiles = false;
    req.includeFiles = true; // Include files so we can filter photos/PDFs
    req.includeUnsupportedMobileFiles = false;
    req.includeCommentCount = false;
    req.pageNumber = 0;

    // If memoRequest has idList, use it (for viewing existing reports)
    if (memoRequest.idList && memoRequest.idList.trim()) {
      // idList is already set, keep it
    } else if (aiResponse?.memoIds && aiResponse.memoIds.trim()) {
      // Use memoIds from AiResponse to recreate the report
      req.idList = aiResponse.memoIds;
    } else if (aiResponse && savedReportMemoIds) {
      req.idList = savedReportMemoIds;
    }
    // Otherwise use the regular filters from memoRequest

    return req;
  }, [memoRequest, aiResponse, savedReportMemoIds]);

  const memoQuery = useMemoQuery({
    filters: reportMemoRequest,
    enabled: enabled && (hasFilterApplied || (!!aiResponse && hasMemoIds)), // Fetch immediately when dialog opens and filter is applied, or if viewing existing report with memoIds
  });

  const memoClassificationQuery = useMemoClassificationQuery({
    enabled: true,
  });

  // Collect and filter photo/PDF files from all memos
  const allPhotoFiles = useMemo<{
    files: File[];
    memoCount: number;
    fileToMemoMap: Map<number, Memo>;
  }>(() => {
    if (!memoQuery.data?.results) return { files: [], memoCount: 0, fileToMemoMap: new Map() };

    const files: File[] = [];
    const memoIdsWithPhotos = new Set<number>();
    const fileToMemoMap = new Map<number, Memo>();

    memoQuery.data.results.forEach((memo) => {
      if (memo.files?.results) {
        let hasPhotos = false;
        memo.files.results.forEach((file) => {
          // Filter out video and audio, only keep images and PDFs
          if (
            file.mimeType &&
            (file.mimeType.includes('image') || file.mimeType.includes('pdf')) &&
            !file.mimeType.includes('video') &&
            !file.mimeType.includes('audio')
          ) {
            files.push(file);
            hasPhotos = true;
            // Map file ID to its parent memo
            if (file.id) {
              fileToMemoMap.set(file.id, memo);
            }
          }
        });
        if (hasPhotos && memo.id) {
          memoIdsWithPhotos.add(memo.id);
        }
      }
    });
    return { files, memoCount: memoIdsWithPhotos.size, fileToMemoMap };
  }, [memoQuery.data?.results]);

  const handleViewAllPhotos = () => {
    setPhotoFiles(allPhotoFiles.files);
    setFileToMemoMap(allPhotoFiles.fileToMemoMap);
    setShowPhotoGallery(true);
  };

  const downloadZipMutation = useDownloadZipMutation({
    onSuccess: () => {
      toast({
        title: 'Download Started',
        description: 'Your photos are being downloaded as a zip file.',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Download Failed',
        description: 'There was an error downloading the photos. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const handleDownloadPhotos = () => {
    if (allPhotoFiles.files.length === 0) {
      toast({
        title: 'No Photos',
        description: 'There are no photos to download.',
        variant: 'default',
      });
      return;
    }

    // Extract file IDs from the photo files
    const fileIds = allPhotoFiles.files
      .map((file) => file.id)
      .filter((id): id is number => id !== undefined && id !== 0);

    if (fileIds.length === 0) {
      toast({
        title: 'No Valid Files',
        description: 'No valid file IDs found for download.',
        variant: 'default',
      });
      return;
    }

    // Create FileIdList request
    const req = FileIdList.create({
      fileIds,
      sizeType: 'original', // or 'compressed' if you want compressed versions
    });

    downloadZipMutation.mutate(req);
  };

  return {
    hasFilterApplied,
    hasMemoIds,
    memoQuery,
    memoClassificationQuery,
    allPhotoFiles,
    showPhotoGallery,
    setShowPhotoGallery,
    photoFiles,
    fileToMemoMap,
    handleViewAllPhotos,
    downloadZipMutation,
    handleDownloadPhotos,
  };
};
